import { SpotlightActionsScope } from '../../app/ActionPlaceScopes';
import { updateBreadcrumbs } from '../../app/actions/UpdateBreadcrumbsAction';
import { appStore } from '../../app/AppStore';
import { BreadcrumbStateInfo } from '../../app/content/breadcrumbs/BreadcrumbStateInfo';
import { SpotlightActionList } from '../../app/content/SpotlightActionList';
import { IMainScope } from '../../app/IMainScope';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { ISubplaceTabsScope, SubplaceEvents } from '../helpers/SubplaceCountsHelper';
import { IGroupInfo } from '../models/IGroupInfo';
import { Module } from '../Module';
import { Groups } from '../resources/Groups';
import { SyncGroupEvent } from '../services/ActiveDirectoryGroupAction';
import { GroupActions, GroupUsersEvent } from '../services/GroupActions';
import { ServerService } from '../services/ServerService';
import { GroupMembershipEvent } from './GroupMembershipDialogCtrl';
import { getGroupsPlaceBreadcrumb } from './GroupsPlaceCtrl';

export interface IGroupPlaceScope extends IMainScope, ISubplaceTabsScope, SpotlightActionsScope {
  group: IGroupInfo;
  canRename: () => boolean;
  rename: ($event: JQueryEventObject) => void;
  synchronize: ($event: JQueryEventObject) => void;
  canSynchronize: () => boolean;
  objectName: string;
}

export class GroupPlaceCtrl {

  public static $inject = [
    '$scope',
    '$translate',
    'Groups',
    'GroupActions',
    'ServerService',
    '$location'
  ];

  constructor(
    private $scope: IGroupPlaceScope,
    $translate: ng.translate.ITranslateService,
    private groupsResource: Groups,
    private groupActions: GroupActions,
    private serverService: ServerService,
    $location: ng.ILocationService,
  ) {
    const group = $scope.group;

    $scope.objectName = group.name;

    $scope.$on(GroupUsersEvent.usersAdded, () => {
      this.refreshGroup();
    });

    $scope.$on(GroupUsersEvent.usersRemoved, () => {
      this.refreshGroup();
    });

    $scope.$on(SyncGroupEvent.groupSynced, () => {
      this.refreshGroup();
    });

    $scope.$on(GroupMembershipEvent.MembershipChanged, () => {
      this.refreshGroup();
    });

    const breadcrumbs: BreadcrumbStateInfo[] = [
      getGroupsPlaceBreadcrumb(),
      { text: group.name }
    ];

    appStore.dispatch(updateBreadcrumbs(breadcrumbs));

    $scope.canRename =
      () => !group.isAllUsersGroup && !this.isActiveDirectoryGroup(group);

    $scope.rename = ($event: JQueryEventObject) => {
      this.groupActions.renameGroup(group, $scope)
        .then(() => this.refreshGroup());
    };

    $scope.canSynchronize =
      () => this.serverService.canSessionUserManageUsers() && !group.isAllUsersGroup && this.isActiveDirectoryGroup(group);

    $scope.synchronize = ($event: JQueryEventObject) => {
      this.groupActions.syncAdGroup(group, this.$scope)
        .then(() =>  {
          this.refreshGroup();
          $scope.$broadcast(SubplaceEvents.Refresh, 'users');
        });
    };

    const renameAction: SpotlightActionList.SpotlightActionAttributes = {
      onClick: $scope.rename,
      iconClass: 'tb-rename-icon',
      isAllowed: $scope.canRename(),
      key: 'setName',
      testId: 'action-rename-place',
      title: $translate.instant('rename_action')
    };

    const refreshAction: SpotlightActionList.SpotlightActionAttributes = {
      onClick: $scope.synchronize,
      iconClass: 'tb-refresh-icon',
      isAllowed: $scope.canSynchronize(),
      key: 'refresh',
      testId: 'action-synchronize-group',
      title: $translate.instant('synchronize_action')
    };

    $scope.SpotlightActionInfo = {
      actions: [renameAction, refreshAction]
    };

    // handles refreshing the tab count and subplace
    $scope.subplaceTabClicked = (name: string) => {
      if (name === 'users') {
        this.refreshGroup();
        $scope.$broadcast(SubplaceEvents.Refresh, name);
      }
    };

    const siteUrls = siteUrlsSelector(appStore.getState());
    $scope.isSelectedTab = (name: string): boolean => {
      if (name === 'users') {
        return siteUrls.groupUsersPath(group.id) === $location.path();
      } else if (name === 'details') {
        return siteUrls.groupDetailsPath(group.id) === $location.path();
      }
      return false;
    };

    $scope.getTabUrl = (name: string): string => {
      if (name === 'users') {
        return siteUrls.groupUsersUrl(group.id);
      } else if (name === 'details') {
        return siteUrls.groupDetailsUrl(group.id);
      }
      return '';
    };
  }

  // fetch the latest group data from server
  private refreshGroup() {
    this.groupsResource.getById(this.$scope.group.id).then((group: IGroupInfo) => {
      if (group) {
        this.$scope.group = group;
      } else {
        // Group may have been deleted, or permissions changed, so reload
        window.location.reload();
      }
    });
  }

  // returns true if the given group is not local
  private isActiveDirectoryGroup(group: IGroupInfo): boolean {
    return (group.domainName && group.domainName !== 'local');
  }

}

Module.instance().controller('GroupPlaceCtrl', GroupPlaceCtrl);
